import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiServices from "../../service";

interface CommissionState {
  listItems: any[];
  filteredData: any;
  selectedProviders: any[];
  providers: { label: string; id: any }[];
  currentSelectedDates: string | null;
  currentSelectedDateLabel: string | null;
  appliedSelectedDates: string | null;
  providerPagination: {
    currentPage: number;
    totalPages: number;
    hasMore: boolean;
  };
  totalRows: number;
  loading: {
    receiptList: boolean;
    providers: boolean;
    postReceipt: boolean;
    updateCommission: boolean;
  };
  error: {
    receiptList: string | null;
    providers: string | null;
    postReceipt: string | null;
    updateCommission: string | null;
  };
}

const initialState: CommissionState = {
  listItems: [],
  filteredData: {},
  selectedProviders: [],
  providers: [],
  currentSelectedDates: null,
  currentSelectedDateLabel: null,
  appliedSelectedDates: null,
  providerPagination: {
    currentPage: 1,
    totalPages: 1,
    hasMore: true,
  },
  totalRows: 0,
  loading: {
    receiptList: false,
    providers: false,
    postReceipt: false,
    updateCommission: false,
  },
  error: {
    receiptList: null,
    providers: null,
    postReceipt: null,
    updateCommission: null,
  },
};

export const fetchProviders = createAsyncThunk(
  "commission/fetchProviders",
  async (
    {
      page = 1,
      limit = 10,
      fetchAll = false,
    }: { page?: number; limit?: number; fetchAll?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const effectiveLimit = fetchAll ? 3000 : limit; // Use a high limit for fetchAll
      const response = await apiServices.systemManager.providers.getList(
        page,
        effectiveLimit,
      );
      if (response.data && response.data.data) {
        const providers = response.data.data.map((item: any) => ({
          label: item.provider_name,
          id: item.id,
        }));
        const totalPages = response.data.pagination?.total_pages || 1;
        return { providers, page, totalPages };
      }
      return { providers: [], page, totalPages: 1 };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch providers");
    }
  },
);

export const fetchReceiptList = createAsyncThunk(
  "commission/fetchReceiptList",
  async (
    {
      userId,
      dateValue,
      page,
      pageSize,
      providers,
    }: {
      userId: string;
      dateValue: string;
      page: number;
      pageSize: number;
      providers: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiServices.commissionModule.receipting.getList(
        userId,
        dateValue,
        page,
        pageSize,
        providers,
      );
      return {
        data: response.data.data || [],
        pagination: {
          currentPage: page,
          totalPages:
            response.data.pagination?.total_pages ||
            Math.ceil(response.data.count / pageSize) ||
            1,
          hasMore:
            page <
            (response.data.pagination?.total_pages ||
              Math.ceil(response.data.count / pageSize) ||
              1),
        },
        totalRows: response.data.count || 0, // Include total count
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch receipt list");
    }
  },
);

export const postReceipt = createAsyncThunk(
  "commission/postReceipt",
  async (
    payload: {
      receiptData: {
        commission_expectation_id: string;
        commission_id: string;
        actual_amount: number;
        receipted_date: any;
        expected_amount?: number;
        due_date?: any;
      }[];
      unused: {
        unused: number;
        receipted_description: string;
        receipted_date: any;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      // Send payload directly to API
      const response =
        await apiServices.commissionModule.receipting.add(payload);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to post receipt actuals",
      );
    }
  },
);

export const updateCommission = createAsyncThunk(
  "commission/updateCommission",
  async (
    { commissionId, clientId, category, formData }: any,
    { rejectWithValue }
  ) => {
    try {
      const caseTypeMap: Record<string, string> = {
        "Unit-linked": "asset_investment_payment_commission",
        Shares: "asset_share_holdings_commission",
        "Banks/B Societies": "asset_banks_building_societies_payment_commission",
        Mortgages: "liabilities_mortgages_payment_commission",
        "Loans/Lease/HP": "liabilities_loan_hire_purchase_payments_commission",
        "Credit Cards": "liabilities_credit_cards_payments_commission",
        General: "policies_general_payments_commission",
        "Life Assurance": "policies_life_assurance_payments_commission",
        Pensions: "policies_pensions_payments_commission",
        Investments: "policies_investments_payments_commission",
        "Savings Plans": "policies_savings_plans_payments_commission",
        "Income Protection": "policies_income_protection_payments_commission",
        "Health Assurance": "policies_health_assurance_commission",
      };

      // 1. FIX: Use if/else to prevent overwriting the key
      let commissionKey;
      if (category === "Other Fees/Charges") {
        commissionKey = "fees_charges";
      } else {
        commissionKey = caseTypeMap[formData.type];
      }

      if (!commissionKey) {
        return rejectWithValue("Invalid commission type/category");
      }

      // 2. FIX: Only include fields in the inner object if they exist in formData
      // This respects your "don't send disabled fields" requirement
      const commissionData: any = {};

      const mapping = [
        { from: "customer_id", to: "customer_id" },
        { from: "case_id", to: "case_id" },
        { from: "payment_id", to: "payment_id" },
        { from: "commission_id", to: "commission_id" },
        { from: "status", to: "status" },
        { from: "commission_type", to: "commission_type" },
        { from: "payment_source", to: "payment_source" },
        { from: "amount_to_company", to: "amount_to_company" }, // Will only add if not disabled
        { from: "commission_frequency", to: "frequency" },
        { from: "commission_startDate", to: "start_date" },
        { from: "commission_endDate", to: "end_date" },
        { from: "payment_count", to: "payment_count" },
        { from: "nb_register", to: "nb_register" },
        { from: "note_text", to: "note_text" },
      ];

      mapping.forEach((item) => {
        if (formData[item.from] !== undefined) {
          // Special handling for Number conversion if necessary
          if (item.to === "amount_to_company" || item.to === "payment_count") {
            commissionData[item.to] = formData[item.from] === "" ? 0 : item.to === "payment_count" ? String(formData[item.from]) : Number(formData[item.from]);
          } else {
            commissionData[item.to] = formData[item.from];
          }
        }
      });

      // Always include module if missing
      commissionData.commission_module = formData.commission_module || false;

      const newData = { [commissionKey]: commissionData };

      console.log("Payload sent to API:", newData);

      let response;
      if (category === "Other Fees/Charges") {
        response = await apiServices.feesCharges.update(commissionId, newData);
      } else {
        response = await apiServices.commission.updateCommission(commissionId, newData);
      }

      if (response?.data && response.data.isError === false) {
        return response.data;
      } else {
        return rejectWithValue(response?.data?.error?.message);
      }
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update commission");
    }
  }
);
export const commissionSlice = createSlice({
  name: "commission",
  initialState,
  reducers: {
    setFilteredData: (state, action: PayloadAction<any>) => {
      state.filteredData = action.payload;
    },
    setSelectedProviders: (
      state,
      action: PayloadAction<{ id: string; name: string }[]>,
    ) => {
      state.selectedProviders = action.payload;
    },
    setCurrentSelectedDates: (
      state,
      action: PayloadAction<{ value: string | null; label: string | null }>,
    ) => {
      state.currentSelectedDates = action.payload.value;
      state.currentSelectedDateLabel = action.payload.label;
    },
    setAppliedSelectedDates: (state, action: PayloadAction<string | null>) => {
      state.appliedSelectedDates = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviders.pending, (state) => {
        state.loading.providers = true;
        state.error.providers = null;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        const { providers, page, totalPages } = action.payload;
        state.providers =
          page === 1 ? providers : [...state.providers, ...providers];
        state.providerPagination = {
          currentPage: page,
          totalPages,
          hasMore: page < totalPages,
        };
        state.loading.providers = false;
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.loading.providers = false;
        state.error.providers = action.payload as string;
      })
      .addCase(fetchReceiptList.pending, (state) => {
        state.loading.receiptList = true;
        state.error.receiptList = null;
      })
      .addCase(fetchReceiptList.fulfilled, (state, action) => {
        state.listItems = action.payload.data;
        state.totalRows = action.payload.totalRows;
        state.providerPagination = action.payload.pagination; // Update pagination
        state.loading.receiptList = false;
      })
      .addCase(fetchReceiptList.rejected, (state, action) => {
        state.loading.receiptList = false;
        state.error.receiptList = action.payload as string;
      })
      .addCase(postReceipt.pending, (state) => {
        state.loading.postReceipt = true;
        state.error.postReceipt = null;
      })
      .addCase(postReceipt.fulfilled, (state) => {
        state.loading.postReceipt = false;
      })
      .addCase(postReceipt.rejected, (state, action) => {
        state.loading.postReceipt = false;
        state.error.postReceipt = action.payload as string;
      })
      .addCase(updateCommission.pending, (state) => {
        state.loading.updateCommission = true;
        state.error.updateCommission = null;
      })
      .addCase(updateCommission.fulfilled, (state) => {
        state.loading.updateCommission = false;
      })
      .addCase(updateCommission.rejected, (state, action) => {
        state.loading.updateCommission = false;
        state.error.updateCommission = action.payload as string;
      });
  },
});

export const {
  setFilteredData,
  setSelectedProviders,
  setCurrentSelectedDates,
  setAppliedSelectedDates,
} = commissionSlice.actions;

export default commissionSlice.reducer;
